import * as React from 'react';
import _ from 'lodash';

import { SelectedOption } from '@tock/shared/utils/option';
import { core } from '@tock/coretypes';
import { pluralize } from '@tock/shared/utils/pluralize';
import { useExperimentVariant } from 'hooks/useExperiment';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import ReceiptLine from 'components/business/receipts/ReceiptLine';

interface OptionsItemizedReceiptSectionProps {
  partySize: number;
  ticketTypePrice?: number;
  ticketTypeName?: string;
  purchaseId?: number;
  options: Dictionary<SelectedOption>;
  showDepositHelpText?: boolean;
  ticketPriceInformation?: core.ITicketPriceInformation | null;
}

const depositHelpText =
  'Your deposit secures your reservation and will be applied to your final bill.';

const minSpendHelpText =
  'Your minimum spend secures your reservation and will be applied to your final bill.';

export const OptionsItemizedReceiptSection: React.FunctionComponent<
  OptionsItemizedReceiptSectionProps
> = ({
  partySize,
  ticketTypePrice,
  ticketTypeName,
  purchaseId,
  options,
  showDepositHelpText,
  ticketPriceInformation,
}) => {
  const disableExperiment = useFeatureFlag('CONSUMER_DISABLE_DEPOSIT_EXPERIMENTS');
  const minimumSpendExperiment =
    useExperimentVariant('DepositDemandExperiment') === 2 && !disableExperiment;

  const expSubText = minimumSpendExperiment ? minSpendHelpText : depositHelpText;
  const isCreditCardHold = ticketPriceInformation?.priceType === 'PAYMENT_CARD_HOLD';
  const tpiPrice = isCreditCardHold
    ? 0
    : ticketPriceInformation?.amountCents || ticketTypePrice || 0;
  const ticketTypeSubtotal =
    ticketPriceInformation?.priceDistribution === 'PER_PARTY' ? tpiPrice : tpiPrice * partySize;

  return (
    <>
      <ReceiptLine
        amount={ticketTypeSubtotal || 0}
        lineItem={
          <>
            {ticketPriceInformation?.priceDistribution === 'PER_PARTY' ? (
              <>{`${ticketTypeName} per party (${pluralize(partySize, 'guest')})`}</>
            ) : (
              <>
                {ticketTypeName}
                <span key={purchaseId}>&nbsp;&times;&nbsp;</span>
                {partySize}
              </>
            )}
          </>
        }
        subText={showDepositHelpText ? expSubText : undefined}
        data-testid={'experience_' + ticketTypeName}
      />
      {_.keys(options).length > 0 &&
        _.map(options, (option: SelectedOption, ticketOptionId: number) => {
          return (
            <ReceiptLine
              key={'option-' + ticketOptionId}
              amount={option.count * option.amount}
              lineItem={
                <>
                  {option.modifier ? <span>&nbsp;&nbsp;</span> : ''}
                  {option.name}
                  {option.count > 1 && <span>&nbsp;&times; {option.count}</span>}
                </>
              }
              data-testid={'option_' + option.name}
            />
          );
        })}
    </>
  );
};

export default OptionsItemizedReceiptSection;
